import type { FearGreedData, VIXData } from "../../services/pulseService";

interface Props {
  fearGreed?: FearGreedData;
  vix?: VIXData;
}

function getRatingColor(rating: string): string {
  switch (rating.toLowerCase()) {
    case "extreme fear": return "text-red-400";
    case "fear": return "text-orange-400";
    case "neutral": return "text-yellow-400";
    case "greed": return "text-accent-400";
    case "extreme greed": return "text-green-300";
    default: return "text-surface-300";
  }
}

function getGaugeColor(score: number): string {
  if (score <= 25) return "#ef4444";
  if (score <= 45) return "#f97316";
  if (score <= 55) return "#eab308";
  if (score <= 75) return "#14b8a6";
  return "#22c55e";
}

export function FearGreedGauge({ fearGreed, vix }: Props) {
  if (!fearGreed) {
    return (
      <div className="glass-card p-4 sm:p-5">
        <h3 className="text-sm font-semibold text-surface-200 uppercase tracking-wider mb-3">Fear & Greed Index</h3>
        <div className="flex items-center justify-center py-8">
          <div className="flex items-center gap-2 text-surface-500 text-xs">
            <div className="h-2 w-2 rounded-full bg-surface-600 animate-pulse" />
            Loading sentiment...
          </div>
        </div>
      </div>
    );
  }

  const angle = (fearGreed.score / 100) * 180 - 90; // -90 to 90 degrees
  const color = getGaugeColor(fearGreed.score);

  return (
    <div className="glass-card p-4 sm:p-5">
      <h3 className="text-sm font-semibold text-surface-200 uppercase tracking-wider mb-3">
        Fear & Greed Index
      </h3>

      <div className="flex flex-col items-center">
        {/* Gauge SVG */}
        <div className="relative w-48 h-28 sm:w-56 sm:h-32">
          <svg viewBox="0 0 200 110" className="w-full h-full">
            {/* Background arc */}
            <path
              d="M 20 100 A 80 80 0 0 1 180 100"
              fill="none"
              stroke="currentColor"
              strokeWidth="12"
              className="text-surface-800"
              strokeLinecap="round"
            />
            {/* Colored gradient segments */}
            <path d="M 20 100 A 80 80 0 0 1 60 38" fill="none" stroke="#ef4444" strokeWidth="12" strokeLinecap="round" opacity="0.6" />
            <path d="M 60 38 A 80 80 0 0 1 100 20" fill="none" stroke="#f97316" strokeWidth="12" strokeLinecap="round" opacity="0.6" />
            <path d="M 100 20 A 80 80 0 0 1 140 38" fill="none" stroke="#eab308" strokeWidth="12" strokeLinecap="round" opacity="0.6" />
            <path d="M 140 38 A 80 80 0 0 1 180 100" fill="none" stroke="#22c55e" strokeWidth="12" strokeLinecap="round" opacity="0.6" />
            {/* Needle */}
            <line
              x1="100"
              y1="100"
              x2="100"
              y2="30"
              stroke={color}
              strokeWidth="3"
              strokeLinecap="round"
              transform={`rotate(${angle} 100 100)`}
              className="transition-all duration-1000 ease-out"
            />
            <circle cx="100" cy="100" r="6" fill={color} />
          </svg>
          {/* Score in center */}
          <div className="absolute bottom-0 left-1/2 -translate-x-1/2 text-center">
            <span className="text-3xl font-bold text-surface-100 tabular-nums">{Math.round(fearGreed.score)}</span>
          </div>
        </div>

        <span className={`text-sm font-semibold mt-2 ${getRatingColor(fearGreed.rating)}`}>
          {fearGreed.rating}
        </span>

        {/* Historical comparison */}
        <div className="grid grid-cols-3 gap-3 mt-4 w-full text-center">
          <div>
            <div className="text-xs text-surface-500">1 Week Ago</div>
            <div className="text-sm font-semibold text-surface-200 tabular-nums">{Math.round(fearGreed.one_week_ago)}</div>
          </div>
          <div>
            <div className="text-xs text-surface-500">1 Month Ago</div>
            <div className="text-sm font-semibold text-surface-200 tabular-nums">{Math.round(fearGreed.one_month_ago)}</div>
          </div>
          <div>
            <div className="text-xs text-surface-500">1 Year Ago</div>
            <div className="text-sm font-semibold text-surface-200 tabular-nums">{Math.round(fearGreed.one_year_ago)}</div>
          </div>
        </div>
      </div>

      {/* VIX section */}
      {vix && (
        <div className="mt-4 pt-4 border-t border-surface-700/50">
          <div className="flex items-center justify-between">
            <span className="text-sm text-surface-400">VIX (Volatility)</span>
            <div className="flex items-center gap-2">
              <span className="text-lg font-bold text-surface-100 tabular-nums">{vix.price.toFixed(2)}</span>
              <span className={`text-xs font-semibold tabular-nums ${vix.change_percent >= 0 ? "text-red-400" : "text-accent-400"}`}>
                {vix.change_percent >= 0 ? "+" : ""}{vix.change_percent.toFixed(2)}%
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
